/* eslint-disable indent */
import {
  Box, Button, Card, Container, Grid, Stack, Table, TableBody,
  TableCell, TableContainer, TablePagination, TableRow, Typography,
} from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import { Avatar } from '@mui/material';

import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import DateTimePicker from 'react-datetime-picker';
import { addMonths, endOfDay, startOfDay, startOfMonth } from 'date-fns';

import PageHeader from 'components/PageHeader/PageHeader';
import MoreMenu from 'components/table/MoreMenu';
import TableHeader from 'components/table/TableHeader';
import SnackbarContext from 'contexts/snackbarContext';
import { handleSelectAllClick, sortByColumn } from 'services/list.service';

import { getCompobotLogs } from 'services/compobot_log.service';
import { CompobotLogType } from 'types/compobot-log.type';
import { getCompobotLogTableHead } from 'constans/table-headers';
import { theme } from '../../theme';

import { getTopUsersByCompobot } from 'services/compobot_log.service';
import { TopUserByCompobotDto } from 'types/top-user-by-compobot.type';

export default function CompobotLogPage() {
  const { t, i18n: { language } } = useTranslation();
  const navigate = useNavigate();
  const { compobotId } = useParams<{ compobotId: string }>(); // route: /compobot/:compobotId/logs
  const { handleOpen } = useContext(SnackbarContext);

  const [page, setPage] = useState(0);
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [orderBy, setOrderBy] = useState<keyof CompobotLogType>('timestamp');
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [selected, setSelected] = useState<string[]>([]);

  const [startDate, setStartDate] = useState(startOfMonth(startOfDay(new Date())));
  const [endDate, setEndDate] = useState(endOfDay(new Date()));
  const [rows, setRows] = useState<CompobotLogType[]>([]);

  const [topUsers, setTopUsers] = useState<TopUserByCompobotDto[]>([]);

  const fetchTopUsers = () => {
    if (!compobotId) return;
    getTopUsersByCompobot(compobotId, { from: startDate, to: endDate, limit: 10 })
      .then((res) => setTopUsers(res.data ?? []))
      .catch(() => handleOpen(t('common.error'), 'error'));
  };

  useEffect(() => {
    if (!compobotId) return;
    fetchLogs();
    fetchTopUsers();
  }, [compobotId]);

  const fetchLogs = () => {
    if (!compobotId) return;
    getCompobotLogs(compobotId, startDate, endDate)
      .then((res) => {
        const mapped: CompobotLogType[] = (res.data ?? []).map((x: any) => ({
          id: x.id,
          timestamp: x.timestamp,
          score: x.score ?? null,
          userName:
            [x.user?.firstname, x.user?.lastname].filter(Boolean).join(' ') ||
            null,
          userEmail: x.user?.email ?? null,
          compobotId: x.compobot?.deviceId || x.compobot?.id || '',
        }));
        setRows(mapped);
      })
      .catch(() => handleOpen(t('common.error'), 'error'));
  };

  const handleRequestSort = (property: keyof CompobotLogType) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const refreshData = () => {
    fetchLogs();
    fetchTopUsers();
  };
  const head = useMemo(() => getCompobotLogTableHead(t), [language, t]);
  const safeRows = rows ?? []; // védelem, bár rows már []
  const sorted = sortByColumn(safeRows, orderBy, order);

  const handlePreview = (row: CompobotLogType) => {
    // ide mehet egy részletes lognézet, vagy a CompoBot oldalára ugrás
    navigate(`/compobot/${row.compobotId}/logs/${row.id}`);
  };

  return (
    <Container>
      <PageHeader
        title={`${t('compobotLogPage.title', { defaultValue: 'Results' })}`}
        hasButton={false}
        hasBackButton
      />

      {/* Szűrők */}
      <Box sx={{ display: 'flex', textAlign: 'center', pb: '10px' }}>
        <Grid container spacing={1}>
          <Grid item xs={12} sm={4}>
            <Typography sx={{ fontSize: 12, color: theme.palette.grey[600] }}>
              {t('formFields.dateFrom')}
            </Typography>
            <DateTimePicker
              value={startDate}
              onChange={(d: Date | null) => d && setStartDate(d)}
              minDetail="year"
              maxDate={endDate}
              disableClock
              clearIcon={null}
              format="yyyy.MM.dd."
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography sx={{ fontSize: 12, color: theme.palette.grey[600] }}>
              {t('formFields.dateTo')}
            </Typography>
            <DateTimePicker
              value={endDate}
              onChange={(d: Date | null) => d && setEndDate(d)}
              minDate={startDate}
              minDetail="year"
              disableClock
              clearIcon={null}
              format="yyyy.MM.dd."
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <Button fullWidth variant="contained" onClick={refreshData}>
              {t('quizResultsPage.refresh')}
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* Kétoszlopos elrendezés: bal = Logok, jobb = Toplista */}
      <Grid container spacing={2}>
        {/* Bal oldal: Log táblázat */}
        <Grid item xs={12} md={7}>
          <Card sx={{ pt: 1 }}>
            <TableContainer>
              <Table>
                <TableHeader
                  order={order}
                  orderBy={orderBy as string}
                  headLabel={head}
                  rowCount={safeRows.length}
                  numSelected={selected.length}
                  onRequestSort={(prop) => handleRequestSort(prop as keyof CompobotLogType)}
                  onSelectAllClick={(checked) => setSelected(handleSelectAllClick(checked, safeRows))}
                  disableSelect
                />

                <TableBody>
                  {sorted
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row) => (
                      <TableRow
                        hover
                        key={row.id}
                        tabIndex={-1}
                        role="checkbox"
                        selected={selected.indexOf(row.id) !== -1}
                        aria-checked={selected.indexOf(row.id) !== -1}
                      >
                        <TableCell component="th" scope="row" padding="none">
                          <Stack direction="row" alignItems="left" spacing={2}>
                            <Typography variant="subtitle2" noWrap sx={{ fontWeight: 600 }}>
                              {new Date(row.timestamp).toLocaleDateString('hu-HU').replaceAll(' ', '')}{' '}
                              {new Date(row.timestamp).toLocaleTimeString('hu-HU')}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell align="left">{row.userName ?? '—'}</TableCell>
                        <TableCell align="left">{row.userEmail ?? '—'}</TableCell>
                        <TableCell align="left">{row.score ?? '—'}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>

                {!rows.length && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                        {t('common.noData', { defaultValue: 'No data' })}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={safeRows.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(e, p) => setPage(p)}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage={t('pagination.rowsPerPage')}
            />
          </Card>
        </Grid>

        {/* Jobb oldal: Top felhasználók */}
        <Grid item xs={12} md={5}>
          <Card sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
              {t('compobotLogPage.topUsersTitle', { defaultValue: 'Toplista' })}
            </Typography>

            <TableContainer>
              <Table size="small" aria-label="top-users">
                <TableBody>
                  {topUsers.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        {t('common.noData', { defaultValue: 'No data' })}
                      </TableCell>
                    </TableRow>
                  )}

                  {topUsers.map((u, idx) => {
                    let bgColor: string | undefined;
                    let trophyColor: string | undefined;

                    if (idx === 0) {
                      bgColor = '#FFF8DC';   // halvány arany háttér
                      trophyColor = '#FFD700'; // arany kupa
                    } else if (idx === 1) {
                      bgColor = '#F5F5F5';   // halvány ezüst háttér
                      trophyColor = '#C0C0C0'; // ezüst kupa
                    } else if (idx === 2) {
                      bgColor = '#FAEBD7';   // halvány bronz háttér
                      trophyColor = '#CD7F32'; // bronz kupa
                    }

                    return (
                      <TableRow key={u.userId} sx={{ backgroundColor: bgColor }}>
                        <TableCell width={32}>
                          {idx < 3 ? (
                            <EmojiEventsIcon sx={{ color: trophyColor }} />
                          ) : (
                            <Typography sx={{ fontWeight: 600 }}>{idx + 1}.</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ fontWeight: 600 }}>
                            {u.name ?? '—'}
                          </Typography>
                          <Typography variant="body2" sx={{ color: theme.palette.grey[600] }}>
                            {u.userEmail ?? '—'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography sx={{ fontWeight: 600 }}>
                            {u.totalScore}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
}